// ─────────────────────────────────────────────────────────────────────────────
// DeleteNodeModal — "Delete Node" from the row menu.
//
// Wired to DELETE /enterprise-nodes/:id. This is a HARD, IRREVERSIBLE purge: the
// node row, its sessions and its bound enrollment tokens are physically deleted.
// There is no soft-deleted copy and no restore endpoint. Audit events, heartbeats
// and metrics are deliberately kept so the node's history outlives it.
//
// WHAT YOU TYPE IS THE NODE'S REGION, not its name. That is the confirmation the
// endpoint actually enforces — it compares the body's `region` against the stored
// one, case- and whitespace-insensitively, because a geoip place name
// ("Île-de-France", "Hesse") is retyped by hand. Confirming against the label
// instead would leave the server's only gate to be satisfied by a value we could
// just read off the row, which is a rubber stamp rather than a check.
//
// The exception is a node whose region is null — geo is geoip-derived and
// unresolved IPs have none. There is nothing to confirm against, so the endpoint
// skips the guard; this falls back to retyping the node's identifier so the action
// still cannot be a stray click, and reports region_verified afterwards.
//
// Every value shown is real. The "disconnect N users" clause is dropped entirely
// when the count is unknown rather than printing a 0 — an invented number in a
// delete confirmation is the worst possible place to guess.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./rowActions.module.css";
import { CloseIcon, TrashIcon, WarnTriangleIcon } from "./icons";
import { DASH, dash, fmtCount, nodeLabel } from "../../../format";
import { deleteNode } from "../../../../../services/nodesService";

export default function DeleteNodeModal({ node, onClose, onDeleted }) {
  const [typed, setTyped]     = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError]     = useState(null);
  const [result, setResult]   = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !deleting) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, deleting]);

  // Which token the admin must retype, and whether it is the server's gate.
  // A stored region means the server will verify it; without one we fall back to
  // the identifier purely as a local are-you-sure.
  const storedRegion = (node.region ?? "").trim();
  const confirmsRegion = storedRegion !== "";
  const label = nodeLabel(node);
  const expected = confirmsRegion ? storedRegion : label;

  // Mirror the server's leniency so the button doesn't stay disabled on an answer
  // the endpoint would have accepted: case, surrounding and inner whitespace, and
  // Unicode composition form all ignored.
  const norm = (s) => s.normalize("NFC").trim().replace(/\s+/g, " ").toLowerCase();
  const confirmed = expected !== "" && norm(typed) === norm(expected);

  // The real per-node user count from the authority row. NOT active_circuits —
  // that counts circuits, comes from the metrics half, and is null with no
  // snapshot, so it would both overstate and randomly vanish.
  const users = node.users;
  const warning = Number.isFinite(users)
    ? `Deleting this node will remove it from the network, revoke all access tokens, and disconnect ${fmtCount(users)} active users.`
    : "Deleting this node will remove it from the network and revoke all access tokens.";

  const rows = {
    "Node ID":    dash(node.node_id),
    "IP Address": dash(node.ip),
    "Region":     dash(node.region),
    "Status":     dash(node.status),
  };

  const confirm = async () => {
    if (!confirmed || deleting) return;

    setDeleting(true);
    setError(null);
    try {
      // Send the region only when there is one to verify; otherwise the endpoint
      // expects an empty body and would 400 on a bogus value.
      const res = await deleteNode(node.node_id, {
        region: confirmsRegion ? typed : null,
      });
      setResult(res);
      onDeleted?.(res);
    } catch (err) {
      // A region mismatch lands here as a 400. The server never echoes the stored
      // value back — by design, so the message can't be used to guess it.
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  // ── Deleted ────────────────────────────────────────────────────────────────
  if (result) {
    return createPortal(
      <div className={styles.overlay} onClick={onClose}>
        <div
          className={`${styles.modal} ${styles.danger}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-node-title"
          onClick={(e) => e.stopPropagation()}
        >
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>

          <h2 className={styles.title} id="delete-node-title">Node deleted</h2>
          <p className={styles.subtitle}>
            {dash(nodeLabel(node))} has been permanently removed.
          </p>

          {result.warnings.map((w) => (
            <p key={w} className={styles.warnText}>{w}</p>
          ))}

          {/* Said out loud: this node had no stored region, so the server had
              nothing to confirm the choice against. */}
          {!result.region_verified && (
            <p className={styles.warnText}>
              This node had no stored region, so the region confirmation was skipped.
            </p>
          )}

          <div className={styles.footer}>
            <button type="button" className={styles.btnPrimary} onClick={onClose}>Done</button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  // ── Confirm ────────────────────────────────────────────────────────────────
  return createPortal(
    <div className={styles.overlay} onClick={deleting ? undefined : onClose}>
      <div
        className={`${styles.modal} ${styles.danger}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-node-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          disabled={deleting}
          aria-label="Close"
        >
          <CloseIcon />
        </button>

        <div className={styles.headRow}>
          <span className={styles.dangerIcon}><WarnTriangleIcon /></span>
          <div>
            <h2 className={styles.title} id="delete-node-title">Delete Node</h2>
            <p className={styles.subtitle}>This action cannot be undone</p>
          </div>
        </div>

        <dl className={styles.summary}>
          {Object.entries(rows).map(([key, value]) => (
            <div key={key} className={styles.row}>
              <dt className={styles.key}>{key}</dt>
              <dd className={styles.value}>{value}</dd>
            </div>
          ))}
        </dl>

        <p className={styles.warnText}>{warning}</p>
        <p className={styles.warnText}>
          The node record, its sessions and its enrollment tokens are destroyed and
          cannot be restored.
        </p>

        <p className={styles.confirmLabel}>
          Type this node&apos;s {confirmsRegion ? "region" : "identifier"}{" "}
          <code className={styles.confirmToken}>{expected || DASH}</code> to confirm deletion
        </p>
        <div className={styles.field}>
          <input
            className={styles.input}
            placeholder={expected}
            autoComplete="off"
            spellCheck={false}
            disabled={deleting}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && confirm()}
            aria-label={`Type the node ${confirmsRegion ? "region" : "identifier"} to confirm deletion`}
          />
        </div>

        {error && <p className={styles.warnText} role="alert">{error}</p>}

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.btnDanger}
            onClick={confirm}
            disabled={!confirmed || deleting}
          >
            <TrashIcon /> {deleting ? "Deleting…" : "Delete Node"}
          </button>
          <button type="button" className={styles.btnGhost} onClick={onClose} disabled={deleting}>
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
